/**
 * WMS Sidebar Menu Configuration
 * Returns menu items filtered by user role in Sofbox sidebar format.
 * Every item (including sub-items) has a unique remixicon icon.
 *
 * Visibility resolution order:
 *   1. ``menu_visibility_overrides`` row in identity-service (admin-set)
 *   2. Hardcoded ``roles: [...]`` array on the item
 *
 * Each item is addressable by a stable key (see ``getMenuKey``); the
 * key shape is the same as what ``/menu-visibility`` returns from the
 * backend, so overrides round-trip without a join table.
 */

const slugify = (s) =>
  String(s || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '') || 'item';

/**
 * Stable identifier for a sidebar item, used as the override key in
 * ``menu_visibility_overrides``.
 *
 *   - Heading row → ``heading:<slug(label)>``
 *   - Top-level group with no path → ``group:<slug(title)>``
 *   - Anything with a path (top-level or child) → the path itself
 */
export const getMenuKey = (item) => {
  if (!item) return '';
  if (item.type === 'heading') return `heading:${slugify(item.label)}`;
  if (item.path) return item.path;
  return `group:${slugify(item.title)}`;
};

const FULL_MENU = [
  // ── Main ──
  { type: 'heading', label: 'Main' },
  {
    title: 'Dashboard',
    icon: 'ri-dashboard-line',
    path: '/dashboard',
    roles: ['ADMIN', 'HOST', 'TENANT', 'STAFF', 'LOGISTICS_PARTNER'],
  },
  // Phase 21 — tenants' "Dashboard" entry above now renders the
  // marketplace search (shop-for-storage is their primary job). The
  // KPI-and-bookings page lives at /my-dashboard, surfaced as
  // "My Dashboard" right below so it stays one click away.
  {
    title: 'My Dashboard',
    icon: 'ri-line-chart-line',
    path: '/my-dashboard',
    roles: ['TENANT'],
  },

  // ── My Storage (TENANT/Customer first) ──
  {
    title: 'My Storage',
    icon: 'ri-archive-drawer-line',
    path: '/my-storage',
    roles: ['TENANT'],
  },

  // ── My Business (for HOST & TENANT) ──
  { type: 'heading', label: 'My Business', roles: ['HOST', 'TENANT'] },
  {
    title: 'My Organization',
    icon: 'ri-building-2-line',
    path: '/my-org',
    roles: ['HOST', 'TENANT'],
  },
  {
    title: 'My Team',
    icon: 'ri-team-line',
    path: '/my-org/team',
    roles: ['HOST', 'TENANT'],
  },
  {
    title: 'My Warehouses',
    icon: 'ri-home-gear-line',
    path: '/my-warehouses',
    roles: ['HOST'],
  },
  {
    title: 'My Billing',
    icon: 'ri-wallet-line',
    path: '/my-billing',
    roles: ['HOST', 'TENANT'],
  },
  // ── Management ──
  { type: 'heading', label: 'Management', roles: ['ADMIN', 'HOST', 'TENANT'] },
  {
    title: 'Warehouses',
    icon: 'ri-building-4-line',
    roles: ['ADMIN', 'HOST'],
    children: [
      { title: 'All Listings', path: '/warehouses', icon: 'ri-list-check-2', roles: ['ADMIN', 'HOST'] },
      { title: 'Onboard New', path: '/warehouses/onboard', icon: 'ri-add-circle-line', roles: ['ADMIN', 'HOST'] },
      // "Configure Layout" lives on each warehouse's row in My
      // Warehouses — point the sidebar at the picker so the user
      // selects which warehouse to configure first. The literal
      // `/warehouses/configure` URL collided with `/warehouses/:id`
      // and triggered a 422 on a `listings/configure` lookup.
      // Hidden from HOST per spec — admins still get the picker.
      { title: 'Configure Layout', path: '/my-warehouses', icon: 'ri-tools-fill', roles: ['ADMIN'] },
      { title: 'Listing Approvals', path: '/warehouses/approvals', icon: 'ri-checkbox-circle-line', roles: ['ADMIN'] },
      { title: 'Map View', path: '/warehouses/map', icon: 'ri-map-pin-line', roles: ['ADMIN'] },
    ],
  },
  {
    // Marketplace group hidden from every persona — admin/host were
    // removed earlier; tenant lost their entry once /dashboard
    // started rendering the marketplace search directly (Phase 21).
    // Routes stay reachable by URL for deep-link / saved searches.
    title: 'Marketplace',
    icon: 'ri-store-3-line',
    roles: [],
    children: [
      { title: 'Search', path: '/tenant/marketplace', icon: 'ri-search-line', roles: [] },
      { title: 'Saved & Watchlist', path: '/marketplace/saved', icon: 'ri-bookmark-line', roles: [] },
    ],
  },
  {
    title: 'Bookings',
    icon: 'ri-calendar-check-line',
    roles: ['ADMIN', 'HOST', 'TENANT'],
    children: [
      { title: 'All Bookings', path: '/bookings', icon: 'ri-book-open-line', roles: ['ADMIN', 'HOST', 'TENANT'] },
      { title: 'Booking Approvals', path: '/bookings/approvals', icon: 'ri-checkbox-circle-line', roles: ['ADMIN', 'HOST'] },
    ],
  },

  {
    title: 'Contracts',
    icon: 'ri-file-paper-2-line',
    roles: ['ADMIN', 'HOST', 'TENANT'],
    children: [
      { title: 'All Contracts', path: '/contracts', icon: 'ri-file-list-2-line', roles: ['ADMIN', 'HOST', 'TENANT'] },
    ],
  },

  // ── Operations ──
  // Heading hidden from HOST per spec — host still sees the Inventory /
  // Space Mgmt groups below, they just no longer ride under an
  // "Operations" label.
  { type: 'heading', label: 'Operations', roles: ['ADMIN', 'TENANT', 'STAFF'] },
  {
    // Inventory hidden from admin per the admin-login spec. Host /
    // tenant / staff still get the operational stock surfaces.
    title: 'Inventory',
    icon: 'ri-archive-line',
    roles: ['HOST', 'TENANT', 'STAFF'],
    children: [
      { title: 'Warehouse Layout', path: '/inventory/warehouse-layout', icon: 'ri-layout-masonry-line', roles: ['HOST', 'STAFF'] },
      { title: 'Stock', path: '/inventory', icon: 'ri-stack-line', roles: ['HOST', 'TENANT', 'STAFF'] },
      { title: 'GRN (Inbound)', path: '/inventory/grn', icon: 'ri-download-2-line', roles: ['HOST', 'STAFF'] },
      { title: 'Putaway Workbench', path: '/inventory/putaway', icon: 'ri-inbox-archive-line', roles: ['HOST', 'STAFF'] },
      { title: 'Dispatch (Outbound)', path: '/inventory/dispatch', icon: 'ri-upload-2-line', roles: ['HOST', 'STAFF'] },
      { title: 'Returns', path: '/inventory/returns', icon: 'ri-arrow-go-back-line', roles: ['HOST', 'STAFF'] },
      { title: 'Damage Register', path: '/inventory/damage-register', icon: 'ri-error-warning-line', roles: ['HOST', 'STAFF'] },
      { title: 'Damage Approvals', path: '/inventory/damage-approvals', icon: 'ri-checkbox-circle-line', roles: [] },
      { title: 'Cycle Count', path: '/inventory/cycle-count', icon: 'ri-clipboard-line', roles: ['HOST', 'STAFF'] },
      { title: 'Stock Alerts', path: '/inventory/stock-alerts', icon: 'ri-alarm-line', roles: ['HOST', 'STAFF'] },
      { title: 'Barcode / QR', path: '/inventory/barcodes', icon: 'ri-barcode-line', roles: ['HOST', 'STAFF'] },
      { title: 'Warehouse Transfer', path: '/inventory/transfers', icon: 'ri-exchange-line', roles: ['HOST', 'STAFF'] },
      { title: 'Batch / Lot', path: '/inventory/batch-tracking', icon: 'ri-hashtag', roles: ['HOST', 'STAFF'] },
      { title: 'Kits & Bundles', path: '/inventory/kits', icon: 'ri-gift-2-line', roles: ['HOST', 'STAFF'] },
    ],
  },
  // ── Procurement ──
  // Hidden from admin per the admin-login spec; STAFF retains
  // access via the leaves it already owned.
  {
    title: 'Procurement',
    icon: 'ri-shopping-cart-2-line',
    roles: ['STAFF'],
    children: [
      { title: 'Products (SKU master)', path: '/procurement/products', icon: 'ri-price-tag-3-line', roles: ['STAFF'] },
      { title: 'Purchase Orders', path: '/procurement/purchase-orders', icon: 'ri-file-list-line', roles: ['STAFF'] },
    ],
  },
  {
    // Space Mgmt hidden from admin per the admin-login spec.
    title: 'Space Mgmt',
    icon: 'ri-layout-masonry-line',
    roles: ['HOST', 'STAFF'],
    children: [
      { title: 'Overview', path: '/spaces', icon: 'ri-pie-chart-line', roles: ['HOST', 'STAFF'] },
      // Phase 21 — Zones + Racks & Bins hidden from the sidebar per
      // vendor spec (storage is map-pin based now). Routes + pages
      // are kept intact for direct URL access and any admin tooling
      // that still needs them.
      { title: 'Zones', path: '/spaces/zones', icon: 'ri-grid-line', roles: [] },
      { title: 'Racks & Bins', path: '/spaces/racks', icon: 'ri-stack-line', roles: [] },
      { title: 'Allocations', path: '/spaces/allocations', icon: 'ri-share-box-line', roles: ['HOST', 'STAFF'] },
    ],
  },
  {
    // Phase 21 — Operations group is ADMIN / STAFF only. Children's
    // role lists were carrying stray HOST entries from earlier
    // iterations; stripped for consistency since the parent-level
    // filter already excludes HOST regardless.
    title: 'Operations',
    icon: 'ri-tools-line',
    roles: ['ADMIN', 'STAFF'],
    children: [
      { title: 'Task Board', path: '/operations/tasks', icon: 'ri-task-line', roles: ['ADMIN', 'STAFF'] },
      { title: 'Dock Schedule', path: '/operations/dock-schedule', icon: 'ri-time-line', roles: ['ADMIN', 'STAFF'] },
      { title: 'Overstay Mgmt', path: '/operations/overstay', icon: 'ri-alarm-warning-line', roles: ['ADMIN'] },
      { title: 'Inbound/Outbound', path: '/operations/tracking', icon: 'ri-route-line', roles: ['ADMIN', 'STAFF'] },
      { title: 'Packaging', path: '/operations/packaging', icon: 'ri-gift-line', roles: ['ADMIN', 'STAFF'] },
      { title: 'Workforce', path: '/operations/workforce', icon: 'ri-group-line', roles: ['ADMIN'] },
    ],
  },
  {
    // Standalone (not nested under Operations) so hosts — who are
    // filtered out of the Operations group — can still reach it.
    title: 'Team Chat',
    icon: 'ri-chat-3-line',
    path: '/operations/team-chat',
    roles: ['ADMIN', 'HOST', 'STAFF'],
  },

  // ── E-Commerce & Fulfillment ──
  // E-Commerce hidden from admin AND tenant per spec — only STAFF and
  // logistics partners still operate the order-sync + fulfillment
  // surfaces. Tenants run pure-storage workflows now.
  { type: 'heading', label: 'E-Commerce', roles: ['STAFF', 'LOGISTICS_PARTNER'] },
  {
    title: 'E-Commerce',
    icon: 'ri-shopping-bag-3-line',
    roles: ['STAFF', 'LOGISTICS_PARTNER'],
    children: [
      { title: 'Order Sync', path: '/ecommerce/order-sync', icon: 'ri-refresh-line', roles: ['LOGISTICS_PARTNER'] },
      { title: 'Fulfillment Center', path: '/ecommerce/fulfillment', icon: 'ri-gift-2-line', roles: ['STAFF', 'LOGISTICS_PARTNER'] },
    ],
  },

  // ── Compliance ──
  // GST & Compliance hidden from admin AND host per spec. Route +
  // page stay reachable for any internal tooling that needs them.
  { type: 'heading', label: 'Compliance', roles: [] },
  {
    title: 'GST & Compliance',
    icon: 'ri-government-line',
    roles: [],
    children: [
      { title: 'GST Filing', path: '/compliance/gst-filing', icon: 'ri-file-chart-line', roles: [] },
    ],
  },

  // ── Finance ──
  { type: 'heading', label: 'Finance', roles: ['ADMIN', 'HOST', 'TENANT'] },
  {
    title: 'Billing',
    icon: 'ri-wallet-3-line',
    roles: ['ADMIN', 'HOST', 'TENANT'],
    children: [
      { title: 'Invoices', path: '/billing/invoices', icon: 'ri-file-list-3-line', roles: ['ADMIN', 'HOST', 'TENANT'] },
      { title: 'Payments', path: '/billing/payments', icon: 'ri-bank-card-line', roles: ['ADMIN', 'TENANT'] },
      { title: 'Rate Cards', path: '/billing/rate-cards', icon: 'ri-price-tag-3-line', roles: ['ADMIN', 'HOST'] },
      { title: 'Payouts', path: '/billing/payouts', icon: 'ri-hand-coin-line', roles: ['ADMIN', 'HOST'] },
      { title: 'My Invoices', path: '/finance/my-invoices', icon: 'ri-file-list-3-line', roles: ['ADMIN', 'HOST'] },
      { title: 'Host Settlements', path: '/finance/host-payouts', icon: 'ri-bank-line', roles: ['ADMIN', 'HOST'] },
      { title: 'My Bank Accounts', path: '/finance/bank-accounts', icon: 'ri-bank-card-2-line', roles: ['HOST'] },
      { title: 'Reversals (claw-back)', path: '/finance/reversals', icon: 'ri-arrow-go-back-line', roles: ['ADMIN'] },
      { title: 'Auto-Debit (eNACH)', path: '/billing/nach-mandates', icon: 'ri-repeat-line', roles: ['ADMIN', 'TENANT'] },
      // Phase 14o — full GST return + ledger stack surfaced here so
      // finance ops sees GSTR-1 / 3B / 9 / tax ledger / e-way bills
      // in one place rather than scattered across two nav groups.
      // Phase 14v — ops compliance dashboard at the top so overdue
      // returns and stale actions surface before ops drills down.
      // Phase 21 — Compliance Calendar through Credit Limits hidden
      // from HOST per spec. ADMIN keeps full access; routes stay
      // reachable by URL for support / migration tooling.
      { title: 'Compliance Calendar', path: '/billing/compliance-calendar', icon: 'ri-calendar-check-line', roles: ['ADMIN'] },
      { title: 'GSTR-1 Report', path: '/billing/gst-report', icon: 'ri-file-chart-line', roles: ['ADMIN'] },
      { title: 'GSTR-3B Summary', path: '/billing/gstr-3b', icon: 'ri-file-text-line', roles: ['ADMIN'] },
      { title: 'GSTR-9 Annual', path: '/billing/gstr-9', icon: 'ri-calendar-line', roles: ['ADMIN'] },
      { title: 'Vendors', path: '/billing/vendors', icon: 'ri-user-star-line', roles: ['ADMIN'] },
      { title: 'Purchase Invoices', path: '/billing/purchase-invoices', icon: 'ri-shopping-cart-2-line', roles: ['ADMIN'] },
      { title: 'GSTR-2B Reconciliation', path: '/billing/gstr-2b', icon: 'ri-git-merge-line', roles: ['ADMIN'] },
      { title: 'Tax Ledger', path: '/billing/tax-ledger', icon: 'ri-book-line', roles: ['ADMIN'] },
      { title: 'E-way Bills', path: '/billing/eway-bills', icon: 'ri-truck-line', roles: ['ADMIN'] },
      { title: 'Credit Limits', path: '/billing/credit-limits', icon: 'ri-bank-card-2-line', roles: ['ADMIN'] },
    ],
  },

  // ── Finance & Settlements ──
  { type: 'heading', label: 'Finance & Settlements', roles: ['ADMIN', 'HOST', 'TENANT'] },
  {
    title: 'Settlements',
    icon: 'ri-exchange-funds-line',
    roles: ['ADMIN', 'HOST', 'TENANT'],
    children: [
      { title: 'Dashboard', path: '/finance', icon: 'ri-dashboard-3-line', roles: ['ADMIN', 'HOST', 'TENANT'] },
      { title: 'Receivables (AR)', path: '/finance/receivables', icon: 'ri-arrow-left-down-line', roles: ['ADMIN', 'HOST'] },
      { title: 'Payables (AP)', path: '/finance/payables', icon: 'ri-arrow-right-up-line', roles: ['ADMIN', 'HOST'] },
      { title: 'Settlement Engine', path: '/finance/settlements', icon: 'ri-refresh-line', roles: ['ADMIN'] },
      // Phase 16 — host-facing settlement statement + admin config.
      { title: 'Settlement Statement', path: '/finance/settlement-statement', icon: 'ri-file-paper-line', roles: ['ADMIN', 'HOST'] },
      { title: 'Rate Cards', path: '/finance/rate-cards', icon: 'ri-percent-line', roles: ['ADMIN'] },
      { title: 'Settlement Settings', path: '/finance/settlement-settings', icon: 'ri-settings-3-line', roles: ['ADMIN'] },
      { title: 'Clawbacks', path: '/finance/clawbacks', icon: 'ri-arrow-go-back-line', roles: ['ADMIN'] },
      { title: 'Rolling Reserve', path: '/finance/rolling-reserve', icon: 'ri-shield-check-line', roles: ['ADMIN'] },
      { title: 'GSTR-8 (TCS)', path: '/finance/gstr-8', icon: 'ri-government-fill', roles: ['ADMIN'] },
      { title: 'Escrow', path: '/finance/escrow', icon: 'ri-safe-2-line', roles: ['ADMIN'] },
      { title: 'Ledger', path: '/finance/ledger', icon: 'ri-book-2-line', roles: ['ADMIN'] },
      { title: 'Bank Recon', path: '/finance/bank-reconciliation', icon: 'ri-bank-line', roles: ['ADMIN'] },
      { title: 'TDS Tracking', path: '/finance/tds', icon: 'ri-government-line', roles: ['ADMIN'] },
    ],
  },

  // ── Communication ──
  { type: 'heading', label: 'Communication', roles: ['ADMIN', 'HOST', 'TENANT', 'LOGISTICS_PARTNER'] },
  {
    title: 'Blog',
    icon: 'ri-article-line',
    path: '/settings/blog',
    roles: ['ADMIN'],
  },
  {
    title: 'Notifications',
    icon: 'ri-notification-3-line',
    roles: ['ADMIN', 'HOST', 'TENANT', 'LOGISTICS_PARTNER'],
    children: [
      { title: 'Inbox', path: '/notifications', icon: 'ri-inbox-line', roles: ['ADMIN', 'HOST', 'TENANT', 'LOGISTICS_PARTNER'] },
      // Hidden — duplicate of the My Settings → Preferences entry
      // (/my-preferences), which already includes the notification
      // toggle matrix. Route stays reachable for any deep links.
      { title: 'Preferences', path: '/notifications/preferences', icon: 'ri-toggle-line', roles: [] },
      { title: 'Templates', path: '/notifications/templates', icon: 'ri-mail-settings-line', roles: ['ADMIN'] },
    ],
  },
  {
    title: 'Reviews & Trust',
    icon: 'ri-star-smile-line',
    roles: ['ADMIN', 'HOST'],
    children: [
      // Host-scoped cockpit — "what tenants said about ME" + reply UX.
      { title: 'My Reviews', path: '/my-reviews', icon: 'ri-chat-smile-line', roles: ['HOST'] },
      { title: 'My Trust Score', path: '/my-trust-score', icon: 'ri-award-line', roles: ['HOST'] },
      // Admin-only firehose across every host on the platform.
      { title: 'All Reviews', path: '/reviews', icon: 'ri-chat-quote-line', roles: ['ADMIN'] },
      { title: 'Trust & Badges', path: '/reviews/trust-management', icon: 'ri-award-line', roles: ['ADMIN'] },
    ],
  },
  {
    // Tenant no longer sees Disputes per spec — admins keep the
    // moderation queue + SLA board. Route stays reachable by URL.
    title: 'Disputes',
    icon: 'ri-shield-cross-line',
    roles: ['ADMIN'],
    children: [
      { title: 'All Disputes', path: '/disputes', icon: 'ri-list-check', roles: ['ADMIN'] },
      { title: 'SLA Board',    path: '/disputes/sla-board', icon: 'ri-dashboard-line', roles: ['ADMIN'] },
    ],
  },
  {
    title: 'Logistics',
    icon: 'ri-truck-line',
    // Gated on the platform admin's `logistics_partner_portal` feature
    // flag — when disabled, the entire Logistics group disappears for
    // every role. Toggle from Settings → Feature Flags.
    // Phase 21 — host AND tenant no longer see the Logistics group
    // per spec. ADMIN / LOGISTICS_PARTNER keep their slices.
    flag: 'logistics_partner_portal',
    roles: ['ADMIN', 'LOGISTICS_PARTNER'],
    children: [
      { title: 'Shipment Lifecycle', path: '/logistics/lifecycle', icon: 'ri-route-line', roles: ['ADMIN', 'LOGISTICS_PARTNER'] },
      { title: 'Partner Onboarding', path: '/logistics/partner-onboarding', icon: 'ri-user-add-line', roles: ['ADMIN', 'LOGISTICS_PARTNER'] },
      { title: 'Partner Management', path: '/logistics/partner-management', icon: 'ri-team-line', roles: ['ADMIN'] },
      { title: 'Freight Requests', path: '/logistics/freight-requests', icon: 'ri-file-transfer-line', roles: ['ADMIN', 'LOGISTICS_PARTNER'] },
      { title: 'Shipments', path: '/logistics/shipments', icon: 'ri-ship-line', roles: ['ADMIN', 'LOGISTICS_PARTNER'] },
      { title: 'Earnings', path: '/logistics/earnings', icon: 'ri-money-cny-circle-line', roles: ['ADMIN', 'LOGISTICS_PARTNER'] },
    ],
  },

  // ── Administration ──
  { type: 'heading', label: 'Administration', roles: ['ADMIN'] },
  {
    title: 'Customers',
    icon: 'ri-building-2-line',
    roles: ['ADMIN'],
    children: [
      { title: 'All Customers', path: '/tenants', icon: 'ri-list-check', roles: ['ADMIN'] },
    ],
  },

  // ── Analytics ──
  { type: 'heading', label: 'Analytics', roles: ['ADMIN', 'HOST'] },
  {
    title: 'Analytics',
    icon: 'ri-bar-chart-box-line',
    roles: ['ADMIN', 'HOST'],
    children: [
      // Host-scoped analytics first so it's the most prominent
      // option in the host's sidebar (admins also see it, but
      // they'll typically jump to Business Intelligence instead).
      { title: 'My Analytics', path: '/analytics/host', icon: 'ri-bar-chart-2-line', roles: ['HOST', 'ADMIN'] },
      { title: 'Business Intelligence', path: '/analytics/business', icon: 'ri-line-chart-line', roles: ['ADMIN'] },
      { title: 'Operations', path: '/analytics/operations', icon: 'ri-pulse-line', roles: ['ADMIN'] },
      { title: 'Financial', path: '/analytics/financial', icon: 'ri-money-rupee-circle-line', roles: ['ADMIN'] },
      { title: 'Platform Health', path: '/analytics/health', icon: 'ri-heart-pulse-line', roles: ['ADMIN'] },
      { title: 'ABC/XYZ Analysis', path: '/analytics/abc-xyz', icon: 'ri-sort-asc', roles: ['ADMIN', 'HOST'] },
      { title: 'Reports Builder', path: '/analytics/reports', icon: 'ri-file-chart-2-line', roles: ['ADMIN', 'HOST'] },
    ],
  },

  // ── Integrations ──
  { type: 'heading', label: 'Integrations', roles: ['ADMIN'] },
  {
    title: 'Integrations',
    icon: 'ri-plug-2-line',
    roles: ['ADMIN'],
    children: [
      { title: 'ONDC Network', path: '/integrations/ondc', icon: 'ri-global-line', roles: ['ADMIN'] },
      { title: 'API Gateway', path: '/integrations/api-gateway', icon: 'ri-shield-keyhole-line', roles: ['ADMIN'] },
    ],
  },

  // ── System ──
  // Heading + Settings group are widened to HOST so host-visible
  // leaves (Contract Defaults, Help & FAQ, API Documentation)
  // actually render in the sidebar. Admin-only leaves keep their
  // own ADMIN role filter and stay hidden from hosts.
  { type: 'heading', label: 'System', roles: ['ADMIN', 'HOST'] },
  {
    title: 'Settings',
    icon: 'ri-settings-4-line',
    roles: ['ADMIN', 'HOST'],
    children: [
      { title: 'Master Data', path: '/settings/master-data', icon: 'ri-database-2-line', roles: ['ADMIN'] },
      { title: 'Users & Roles', path: '/settings/users', icon: 'ri-user-settings-line', roles: ['ADMIN'] },
      { title: 'KYC Queue', path: '/settings/kyc-queue', icon: 'ri-shield-check-line', roles: ['ADMIN'] },
      { title: 'Moderation', path: '/settings/moderation', icon: 'ri-flag-2-line', roles: ['ADMIN'] },
      { title: 'Warehouse Requests', path: '/settings/enquiries', icon: 'ri-customer-service-2-line', roles: ['ADMIN'] },
      { title: 'Pricing Governance', path: '/settings/pricing', icon: 'ri-price-tag-3-line', roles: ['ADMIN'] },
      // Marketing copy for the public /pricing page — tiers, bullets,
      // comparison rows. Distinct from "Pricing Governance" (which
      // controls actual booking commission rules).
      { title: 'Public Pricing Page', path: '/settings/public-pricing', icon: 'ri-edit-box-line', roles: ['ADMIN'] },
      { title: 'Warehouse Rent Index', path: '/settings/rent-index', icon: 'ri-line-chart-line', roles: ['ADMIN'] },
      { title: 'Platform Config', path: '/settings/platform', icon: 'ri-equalizer-line', roles: ['ADMIN'] },
      { title: 'Branding', path: '/settings/branding', icon: 'ri-palette-line', roles: ['ADMIN'] },
      { title: 'Integrations', path: '/settings/integrations', icon: 'ri-plug-line', roles: ['ADMIN'] },
      { title: 'Business Rules', path: '/settings/business-rules', icon: 'ri-scales-3-line', roles: ['ADMIN'] },
      { title: 'Notifications', path: '/settings/notification-templates', icon: 'ri-mail-settings-line', roles: ['ADMIN'] },
      { title: 'Doc Templates', path: '/settings/document-templates', icon: 'ri-file-copy-2-line', roles: ['ADMIN'] },
      { title: 'Contract Clauses', path: '/settings/contract-clauses', icon: 'ri-draft-line', roles: ['ADMIN'] },
      // Host-visible: each host's own commercial-terms overrides
      // on top of the platform's clauses. Hosts can't edit the
      // admin's clause templates but they control the numbers
      // (late fee, termination notice, etc.) and can add their own
      // clauses for warehouse-specific rules.
      { title: 'Contract Defaults', path: '/settings/contract-defaults', icon: 'ri-file-edit-line', roles: ['ADMIN', 'HOST'] },
      // Phase-2a template composer — narrative body authoring
      // (recitals, obligations, liability prose). Complements
      // Contract Defaults (numbers) above: together they cover
      // every host-customisable piece of a contract.
      { title: 'Contract Templates', path: '/settings/contract-templates', icon: 'ri-article-line', roles: ['ADMIN', 'HOST'] },
      // Per-host branding for the 6 PDF document types (invoice, GST
      // report, rate card, payment schedule, credit note, receipt).
      // Moved here from "My Business" — it covers more than invoices
      // and conceptually sits with the other template editors below.
      { title: 'Document Branding', path: '/settings/provider-templates', icon: 'ri-file-text-line', roles: ['HOST'] },
      // Phase 14j.1.1 — named presets for EWB generation (consignor
      // defaults, default transporter, default HSN + distance).
      { title: 'E-way Bill Templates', path: '/settings/eway-bill-templates', icon: 'ri-truck-line', roles: ['ADMIN', 'HOST'] },
      // Admin-only review queue — host saves with unlocked
      // sections land here awaiting legal sign-off.
      { title: 'Template Review Queue', path: '/settings/contract-review', icon: 'ri-shield-check-line', roles: ['ADMIN'] },
      // Team Members — host-facing fulfillment staff management.
      // Replaces the hardcoded WAREHOUSE_STAFF const that previously
      // lived in the ecommerce fulfillment flow with real backend
      // data. Admins can cross-host via query param for support.
      { title: 'Team Members', path: '/settings/team-members', icon: 'ri-team-line', roles: ['ADMIN', 'HOST'] },
      // Phase 19c — host can author named fulfillment roles
      // ("Day Shift Picker", "QC Lead") that wrap the 4 platform
      // base roles. Used in the Team Members invite dropdown.
      { title: 'Fulfillment Roles', path: '/settings/fulfillment-roles', icon: 'ri-shield-user-line', roles: ['ADMIN', 'HOST'] },
      { title: 'Landing Page', path: '/settings/landing-page', icon: 'ri-pages-line', roles: ['ADMIN'] },
      { title: 'Listing Fields', path: '/settings/listing-fields', icon: 'ri-layout-grid-line', roles: ['ADMIN'] },
      { title: 'DPDP Compliance', path: '/settings/dpdp-compliance', icon: 'ri-shield-star-line', roles: ['ADMIN'] },
      { title: 'Help & FAQ', path: '/settings/faq-management', icon: 'ri-question-answer-line', roles: ['ADMIN', 'HOST'] },
      { title: 'Audit Logs', path: '/settings/audit-logs', icon: 'ri-file-search-line', roles: ['ADMIN'] },
      { title: 'SLA Management', path: '/settings/sla', icon: 'ri-timer-line', roles: ['ADMIN'] },
      { title: 'Feature Flags', path: '/settings/feature-flags', icon: 'ri-toggle-line', roles: ['ADMIN'] },
      { title: 'Menu Visibility', path: '/settings/menu-visibility', icon: 'ri-menu-search-line', roles: ['ADMIN'] },
      { title: 'Roles & Permissions', path: '/settings/roles-permissions', icon: 'ri-shield-keyhole-line', roles: ['ADMIN'] },
{ title: 'Webhooks & APIs', path: '/settings/webhooks', icon: 'ri-webhook-line', roles: ['ADMIN'] },
      { title: 'Notifications Mgmt', path: '/settings/notification-dashboard', icon: 'ri-message-3-line', roles: ['ADMIN'] },
      { title: 'Workflow Engine', path: '/settings/workflow-engine', icon: 'ri-git-branch-line', roles: ['ADMIN'] },
      { title: 'Compliance Tools', path: '/settings/compliance-tools', icon: 'ri-shield-check-line', roles: ['ADMIN'] },
      { title: 'Scheduled Reports', path: '/settings/scheduled-reports', icon: 'ri-calendar-schedule-line', roles: ['ADMIN'] },
      { title: 'Anomaly Alerts', path: '/settings/anomaly-alerts', icon: 'ri-alarm-warning-line', roles: ['ADMIN'] },
      { title: 'Support Tickets', path: '/settings/support-tickets', icon: 'ri-customer-service-2-line', roles: ['ADMIN'] },
      { title: 'Data Import/Export', path: '/settings/data-import-export', icon: 'ri-upload-cloud-2-line', roles: ['ADMIN'] },
      { title: 'SMS & WhatsApp', path: '/settings/sms-whatsapp', icon: 'ri-message-2-line', roles: ['ADMIN'] },
      { title: 'Payment Gateway', path: '/settings/payment-gateway', icon: 'ri-secure-payment-line', roles: ['ADMIN'] },
      // API Documentation hidden everywhere per spec. Route stays
      // reachable for any deep links.
      { title: 'API Documentation', path: '/settings/api-docs', icon: 'ri-code-s-slash-line', roles: [] },
      { title: 'Changelog', path: '/settings/changelog', icon: 'ri-history-line', roles: ['ADMIN'] },
    ],
  },

  // ── My Settings (for all non-admin roles) ──
  { type: 'heading', label: 'My Settings', roles: ['HOST', 'TENANT', 'STAFF', 'LOGISTICS_PARTNER'] },
  {
    title: 'My Profile',
    icon: 'ri-user-3-line',
    path: '/profile',
    roles: ['HOST', 'TENANT', 'STAFF', 'LOGISTICS_PARTNER'],
  },
  {
    title: 'Preferences',
    icon: 'ri-user-settings-line',
    path: '/my-preferences',
    roles: ['HOST', 'TENANT', 'STAFF', 'LOGISTICS_PARTNER'],
  },
  {
    title: 'Theme',
    icon: 'ri-palette-line',
    path: '/settings/theme',
    roles: ['HOST', 'TENANT', 'STAFF', 'LOGISTICS_PARTNER'],
  },
  {
    title: 'Help Center',
    icon: 'ri-customer-service-2-line',
    path: '/help-center',
    roles: ['HOST', 'TENANT', 'STAFF', 'LOGISTICS_PARTNER'],
  },
  {
    // API Documentation hidden per spec. Route + page intact.
    title: 'API Documentation',
    icon: 'ri-code-s-slash-line',
    path: '/settings/api-docs',
    roles: [],
  },
];

/**
 * Returns sidebar menu items filtered by the given user role.
 * @param {string} role - One of ADMIN, HOST, TENANT, STAFF, LOGISTICS_PARTNER
 * @returns {Array} Filtered menu items
 */
/**
 * Per-item visibility check. Returns true iff the item should be
 * shown to ``role``, after consulting the (optional) overrides map.
 *
 * @param {object} item        — menu item (heading, group, or leaf)
 * @param {string} role        — UPPERCASE canonical role
 * @param {Map<string,boolean>} overrides — keyed by ``getMenuKey(item)``
 */
const isItemVisible = (item, role, overrides, isFlagEnabled) => {
  const key = getMenuKey(item);
  // Feature-flag gate runs first — a disabled flag hides the item even
  // when the per-role override would have shown it. Flags are admin-
  // controlled and represent a global product on/off, so they should
  // win over role-based overrides.
  if (item.flag && isFlagEnabled && !isFlagEnabled(item.flag, true)) {
    return false;
  }
  if (overrides && overrides.has(key)) return overrides.get(key);
  // Fallback: hardcoded roles array on the item.
  return !item.roles || item.roles.includes(role);
};

/**
 * Returns the role-filtered menu, optionally honouring admin-set
 * overrides from ``GET /master-data/menu-visibility/me``.
 *
 * ``overrides`` may be a ``Map<key, boolean>``, a plain object, or
 * an array of ``{menu_key, is_visible}`` rows (the wire format) —
 * normalised here so callers don't have to.
 */
export const getWMSSidebarItems = (role = 'TENANT', overrides = null, isFlagEnabled = null) => {
  let upperRole = (role || 'TENANT').toUpperCase();
  // Normalise role aliases that the backend may emit but the menu
  // config doesn't list directly. Keeps the config readable (one
  // canonical role tag per persona) while accepting whatever the
  // identity service sends on the JWT.
  if (upperRole === 'SUPER_ADMIN') upperRole = 'ADMIN';
  if (upperRole === 'WAREHOUSE_STAFF') upperRole = 'STAFF';

  // Normalise to a Map<string, boolean> so the per-item check is O(1).
  let overrideMap = null;
  if (overrides instanceof Map) {
    overrideMap = overrides;
  } else if (Array.isArray(overrides)) {
    overrideMap = new Map();
    overrides.forEach((row) => {
      if (row && row.menu_key) overrideMap.set(row.menu_key, !!row.is_visible);
    });
  } else if (overrides && typeof overrides === 'object') {
    overrideMap = new Map(Object.entries(overrides).map(([k, v]) => [k, !!v]));
  }

  return FULL_MENU.reduce((acc, item) => {
    if (item.type === 'heading') {
      if (isItemVisible(item, upperRole, overrideMap, isFlagEnabled)) acc.push(item);
      return acc;
    }

    if (!isItemVisible(item, upperRole, overrideMap, isFlagEnabled)) return acc;

    if (item.children) {
      const filteredChildren = item.children.filter((child) =>
        isItemVisible(child, upperRole, overrideMap, isFlagEnabled),
      );
      if (filteredChildren.length > 0) {
        acc.push({ ...item, children: filteredChildren });
      }
    } else {
      acc.push(item);
    }

    return acc;
  }, []);
};

/**
 * Flatten the full sidebar config into a list of rows for the admin
 * "Menu visibility" grid. Each row has ``key`` (the address used in
 * overrides), ``label`` (display string), ``kind`` (heading / group /
 * leaf), ``parentKey`` (for tree indentation in the UI), and
 * ``defaultRoles`` (the hardcoded fallback the admin can override).
 */
export const flattenMenu = () => {
  const rows = [];
  FULL_MENU.forEach((item) => {
    if (item.type === 'heading') {
      rows.push({
        key: getMenuKey(item),
        label: item.label,
        kind: 'heading',
        parentKey: null,
        defaultRoles: item.roles || null,
      });
      return;
    }
    const groupKey = getMenuKey(item);
    rows.push({
      key: groupKey,
      label: item.title,
      kind: item.children ? 'group' : 'leaf',
      parentKey: null,
      defaultRoles: item.roles || null,
    });
    (item.children || []).forEach((child) => {
      rows.push({
        key: getMenuKey(child),
        label: child.title,
        kind: 'leaf',
        parentKey: groupKey,
        defaultRoles: child.roles || null,
      });
    });
  });
  return rows;
};

/** Canonical role tags shown as columns in the admin grid. */
export const SIDEBAR_ROLES = ['ADMIN', 'HOST', 'TENANT', 'STAFF', 'LOGISTICS_PARTNER'];

export default getWMSSidebarItems;
